import { useEffect, useState, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { TrendingUp, AlertTriangle, Users, MapPin, Layers, Sparkles, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import Layout from '../components/Layout';
import CompanyFilter from '../components/CompanyFilter';
import { api, formatCurrency } from '../lib/api';

// City coordinates for India map markers
const CITY_COORDS = {
  Mumbai: [72.8777, 19.0760],
  Delhi: [77.1025, 28.7041],
  Bangalore: [77.5946, 12.9716],
  Hyderabad: [78.4867, 17.3850],
  Chennai: [80.2707, 13.0827],
  Kolkata: [88.3639, 22.5726],
  Pune: [73.8567, 18.5204],
  Ahmedabad: [72.5714, 23.0225],
  Gurgaon: [77.0266, 28.4595],
  Noida: [77.3910, 28.5355],
  Jaipur: [75.7873, 26.9124],
  Lucknow: [80.9462, 26.8467],
  Indore: [75.8577, 22.7196],
  Chandigarh: [76.7794, 30.7333],
  Kochi: [76.2673, 9.9312],
  Goa: [73.8278, 15.2993],
};

const KPI = ({ label, value, sub, accent = false, testId }) => (
  <div data-testid={testId} className={`bg-surface border border-border p-5 ${accent ? 'border-l-2 border-l-maroon' : ''} fade-up`}>
    <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary font-semibold mb-3">{label}</p>
    <p className="font-numbers text-2xl font-light text-text-primary tabular-nums">{value}</p>
    {sub && <p className="font-body text-xs text-text-secondary mt-1">{sub}</p>}
  </div>
);

const SectionTitle = ({ tag, title, action }) => (
  <div className="flex items-baseline justify-between mb-4">
    <div>
      <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-1">— {tag}</p>
      <h2 className="font-heading text-xl font-light text-text-primary">{title}</h2>
    </div>
    {action}
  </div>
);

const ClientIntelligence = () => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingSummary, setRefreshingSummary] = useState(false);

  const load = async (refresh = false) => {
    const params = {};
    if (selectedCompany) params.company_id = selectedCompany;
    if (refresh) params.refresh = true;
    const r = await api.get('/dashboard/client-intelligence', { params });
    setData(r.data);
  };

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setLoading(true);
      load().finally(() => { if (!cancelled) setLoading(false); });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const refreshSummary = async () => {
    setRefreshingSummary(true);
    try { await load(true); } finally { setRefreshingSummary(false); }
  };

  const fmtL = (n) => {
    const v = Number(n) || 0;
    if (v >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
    if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
    return `₹${v.toFixed(0)}`;
  };

  const concentrationColor = useMemo(() => {
    if (!data) return 'text-text-primary';
    return { green: 'text-success', yellow: 'text-warning', red: 'text-maroon' }[data.concentration.status];
  }, [data]);

  if (loading || !data) {
    return <Layout title="Client Intelligence" filter={<CompanyFilter value={selectedCompany} onChange={setSelectedCompany} />}><p className="text-text-secondary">Loading…</p></Layout>;
  }

  const e = data.executive;
  const ins = data.map.insights;

  return (
    <Layout title="Client Intelligence" filter={<CompanyFilter value={selectedCompany} onChange={setSelectedCompany} />}>
      {/* SECTION 1 — Executive Summary */}
      <SectionTitle tag="Snapshot" title="Executive Summary" />
      <div data-testid="ci-executive" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
        <KPI testId="ci-kpi-new-clients" label="New Clients · This Month" value={e.new_clients_this_month} sub={e.new_clients_delta >= 0 ? `+${e.new_clients_delta} vs last month` : `${e.new_clients_delta} vs last month`} />
        <KPI testId="ci-kpi-avg-retainer" label="Average Retainer" value={fmtL(e.avg_retainer)} sub="across active clients" />
        <KPI testId="ci-kpi-rev-existing" label="Revenue · Existing" value={fmtL(e.revenue_existing_clients_this_month)} sub={`${e.revenue_existing_pct}% of billing`} />
        <KPI testId="ci-kpi-rev-new" label="Revenue · New" value={fmtL(e.revenue_new_clients_this_month)} sub={`${e.revenue_new_pct}% of billing`} />
        <KPI testId="ci-kpi-largest-segment" label="Largest Segment" value={e.largest_segment || '—'} sub={`${e.largest_segment_pct}% of revenue`} accent />
      </div>

      {/* SECTION 2 — India Map */}
      <SectionTitle tag="Geography" title="India Client Intelligence Map" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="bg-surface border border-border p-4">
          <p className="font-body text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-1">Largest Market</p>
          <p className="font-heading text-lg text-text-primary">{ins.largest_market || '—'}</p>
        </div>
        <div className="bg-surface border border-border p-4">
          <p className="font-body text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-1">Highest Avg Retainer</p>
          <p className="font-heading text-lg text-text-primary">{ins.highest_retainer_market || '—'}</p>
        </div>
        <div className="bg-surface border border-border p-4">
          <p className="font-body text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-1">Fastest Growing</p>
          <p className="font-heading text-lg text-text-primary">{ins.fastest_growing || '—'}</p>
        </div>
      </div>

      <div data-testid="ci-india-map" className="bg-surface border border-border p-4 mb-10" style={{ minHeight: 480 }}>
        <ComposableMap projection="geoMercator" projectionConfig={{ center: [82, 22], scale: 850 }} style={{ width: '100%', height: 480 }}>
          <Geographies geography="/india.topo.json">
            {({ geographies }) => geographies.map(geo => (
              <Geography key={geo.rsmKey} geography={geo} fill="#f4ede2" stroke="#d4c8b6" strokeWidth={0.4} style={{ default: { outline: 'none' }, hover: { fill: '#ece1ce', outline: 'none' }, pressed: { outline: 'none' } }} />
            ))}
          </Geographies>
          {data.map.cities.filter(c => CITY_COORDS[c.city]).map(c => {
            const r = Math.min(20, 6 + Math.sqrt(c.clients) * 5);
            return (
              <Marker key={c.city} coordinates={CITY_COORDS[c.city]} data-testid={`ci-marker-${c.city}`}>
                <circle r={r} fill="#7B1B1B" fillOpacity={0.55} stroke="#7B1B1B" strokeWidth={1} />
                <title>{`${c.city}\nClients: ${c.clients}\nRevenue: ${formatCurrency(c.revenue)}\nAvg Retainer: ${formatCurrency(c.avg_retainer)}`}</title>
                <text textAnchor="middle" y={-r - 4} className="text-[10px] font-medium" style={{ fill: '#1a1a1a' }}>{c.city}</text>
              </Marker>
            );
          })}
        </ComposableMap>
      </div>

      {/* SECTION 3 — Segmentation */}
      <SectionTitle tag="Mix" title="Client Segmentation" />
      <div data-testid="ci-segments" className="bg-surface border border-border p-6 mb-10">
        <table className="w-full">
          <thead>
            <tr>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Segment</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Clients</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Retainers</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Coverage</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Total / mo</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Avg Retainer</th>
            </tr>
          </thead>
          <tbody>
            {data.segments.map(s => (
              <tr key={s.segment} className="border-b border-border">
                <td className="py-3 font-body text-sm text-text-primary">{s.segment}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{s.clients}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{s.retainer_count ?? 0}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums text-maroon">{s.retainer_coverage_pct ?? 0}%</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(s.total_monthly_retainer ?? 0)}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(s.avg_retainer)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SECTION 4 — Growth */}
      <SectionTitle tag="Growth" title="New Client Trend" />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-10">
        <div className="lg:col-span-3 bg-surface border border-border p-6" style={{ minHeight: 280 }}>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.growth.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9e4dd" />
              <XAxis dataKey="label" stroke="#777" style={{ fontSize: 11 }} />
              <YAxis stroke="#777" style={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#7B1B1B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-surface border border-border p-5 flex flex-col justify-center">
          <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary font-semibold mb-3">Avg Client Age</p>
          <p className="font-numbers text-4xl font-light text-text-primary tabular-nums">{data.growth.avg_client_age_months}</p>
          <p className="font-body text-xs text-text-secondary mt-1">months · active clients</p>
        </div>
      </div>

      {/* SECTION 6 — Top Cities */}
      <SectionTitle tag="Cities" title="Top Cities by Revenue" />
      <div data-testid="ci-top-cities" className="bg-surface border border-border p-6 mb-10">
        <table className="w-full">
          <thead>
            <tr>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left w-12">#</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">City</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Clients</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Revenue</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Avg Retainer</th>
            </tr>
          </thead>
          <tbody>
            {data.top_cities.map((c, i) => (
              <tr key={c.city} className="border-b border-border">
                <td className="py-3 font-numbers text-sm text-text-secondary tabular-nums">{i + 1}</td>
                <td className="py-3 font-body text-sm text-text-primary">{c.city}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{c.clients}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(c.revenue)}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(c.avg_retainer)}</td>
              </tr>
            ))}
            {data.top_cities.length === 0 && <tr><td colSpan="5" className="py-6 text-center text-text-secondary font-body text-sm">No city data — add an Address with city to your clients.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* SECTION 7 — Risk & Concentration */}
      <SectionTitle tag="Risk" title="Concentration & At-Risk" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div data-testid="ci-concentration" className="bg-surface border border-border p-6 flex flex-col justify-center">
          <p className="font-body text-[10px] uppercase tracking-[0.25em] text-text-secondary font-semibold mb-3">Top 5 Clients · Revenue Share</p>
          <p className={`font-numbers text-5xl font-light tabular-nums ${concentrationColor}`}>{data.concentration.top5_pct}%</p>
          <p className="font-body text-xs text-text-secondary mt-2">{data.concentration.status === 'green' ? 'Healthy diversification' : data.concentration.status === 'yellow' ? 'Moderate concentration' : 'High concentration risk'}</p>
        </div>
        <div data-testid="ci-at-risk" className="lg:col-span-2 bg-surface border border-border p-6">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-maroon" strokeWidth={1.5} /><h3 className="font-heading text-base font-medium text-text-primary">At-Risk Clients</h3><span className="font-body text-xs text-text-secondary ml-auto">{data.at_risk.length}</span></div>
          {data.at_risk.length === 0 ? <p className="font-body text-sm text-text-secondary">Nothing flagged. Nice.</p> : (
            <table className="w-full">
              <thead><tr><th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-2 text-left">Client</th><th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-2 text-left">Risk</th><th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-2 text-right">LTV</th></tr></thead>
              <tbody>
                {data.at_risk.map(r => (
                  <tr key={r.client_id} className="border-b border-border">
                    <td className="py-3 font-body text-sm text-text-primary">{r.client_name}</td>
                    <td className="py-3 font-body text-xs text-maroon">{r.reasons.join(' · ')}</td>
                    <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(r.lifetime_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* SECTION 8 — AI Summary */}
      <SectionTitle tag="Brief" title="AI Monthly Intelligence Summary" action={<button data-testid="ci-refresh-summary" onClick={refreshSummary} disabled={refreshingSummary} className="font-body text-xs uppercase tracking-widest text-text-secondary hover:text-maroon flex items-center transition-colors disabled:opacity-50"><RefreshCw className={`w-3 h-3 mr-1 ${refreshingSummary ? 'animate-spin' : ''}`} strokeWidth={1.5} /> {refreshingSummary ? 'Refreshing…' : 'Refresh'}</button>} />
      <div data-testid="ci-ai-summary" className="bg-cream border-l-2 border-l-maroon border border-border p-6 mb-6">
        <div className="flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-maroon mt-1 flex-shrink-0" strokeWidth={1.5} />
          <div className="font-body text-sm text-text-primary leading-relaxed whitespace-pre-line">
            {data.ai_summary || 'No AI summary available yet — click Refresh to generate one.'}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClientIntelligence;
